import { resolveVerticalAlign } from '@lite-workspace/shared/render-model';
import type { RenderTextBox, TextRun } from '@lite-workspace/shared/canvas-engine/core';
import {
  wrapLinesDetailed,
  wrapLinesShapedDetailed,
  buildAreaShapeLineSpans,
  visibleLineText,
  DEFAULT_HYPHENATION_RULES,
  justifySqueezeRatio,
  wrapSlackPx,
  textLayoutAtLeast,
  TEXT_LAYOUT_FRAME_BASELINE,
  TEXT_LAYOUT_INDENT_BAND,
  TEXT_LAYOUT_LINE_STACK,
  TEXT_LAYOUT_OPTICAL_KERN,
  TEXT_LAYOUT_PARAGRAPHS,
  TEXT_LAYOUT_PEN_LINES,
  TEXT_LAYOUT_RUN_FONTS,
  TEXT_LAYOUT_RUN_LANGUAGE,
  WordLanguages,
  type AreaLineSpan,
  type HyphenationRules,
  type LineInk,
  type SlotMetrics,
  maxInk,
  type WrappedLine,
  isForcedLineEnd,
  PARAGRAPH_OR_SOFT_BREAK,
  SOFT_LINE_BREAK,
} from '@lite-workspace/text-layout';
import {
  PSD_SUPERSCRIPT_SIZE_DEFAULT,
  PSD_SUBSCRIPT_SIZE_DEFAULT,
  PSD_SUPERSCRIPT_POSITION_DEFAULT,
  PSD_SUBSCRIPT_POSITION_DEFAULT,
  PSD_SMALL_CAP_SIZE_DEFAULT,
} from '@lite-workspace/dto-fotoclass-templates';
/* eslint-disable @nx/enforce-module-boundaries -- leaf modules; the barrel pulls Angular/Konva and breaks this lib's vitest */
import { applyFontCapsSegments } from '../../../canvas-engine/src/lib/svg/font-caps-segments';
/* eslint-enable @nx/enforce-module-boundaries */
import { buildPathSampler } from '@lite-workspace/photobook-model';
import { textMatrixAnisotropy, textMatrixScale } from './text-affine';
import { fauxBoldAdvanceEm } from './faux-bold';
import type { GlyphDecoration, LaidOutGlyph } from './text-scene-adapter';
import type { RunGlyph, Shaper, ShaperOpts } from './glyph-shaper';
import { TextParagraphs, type LineParagraph, type WrapPlan } from './text-paragraphs';

export type FontLayoutOptions = ShaperOpts;

export function resolveCapHeightRatio(shaper: Shaper, fontRef: string): number {
  const upm = shaper.unitsPerEm(fontRef) || 1000;
  // Decorative fonts under-declare sCapHeight; digits/caps glyphs often sit higher — take the max (svg-text-generator parity).
  let maxYMax = shaper.capHeightUnits(fontRef);
  for (const ch of ['H', '0']) {
    const id = shaper.charGlyphId(fontRef, ch);
    if (id === 0) continue;
    const yMax = shaper.glyphYMax(fontRef, ch);
    if (yMax > maxYMax) maxYMax = yMax;
  }

  return maxYMax > 0 ? maxYMax / upm : 0.7;
}

/** First-line vertical metrics in px (at `fontSize`): where the engine puts the cap-line and its decorative pull-up. */
export interface FirstLineCapMetrics {
  /** cap-height = resolveCapHeightRatio × fontSize — the baseline↔cap-top distance the engine draws to. */
  capHeightPx: number;
  /** Cap minus ink ascent: signed for ink-top-anchored point text, else it only pulls shorter ink up (≥ 0). */
  ascenderDeltaPx: number;
}

/**
 * The vertical metrics the engine lays the FIRST line to, from that line's rendered (fontCaps-applied) segments.
 * SSoT for both the renderer (`layoutGlyphsBackend`) and the inline-edit caret overlay: the caret can only track
 * the glyphs if it derives cap-height from the SAME source the engine draws from — a second, independently measured
 * cap-height (e.g. Canvas `measureText('H')`) drifts the caret off the text (it ignores the '0'/sCapHeight max).
 */
export function firstLineCapMetrics(
  segsFirstLine: ReadonlyArray<{ text: string; fontSizeScale: number }>,
  shaper: Shaper,
  fontRef: string,
  fontSize: number,
  inkTopAnchored = false,
): FirstLineCapMetrics {
  const capHeightPx = resolveCapHeightRatio(shaper, fontRef) * fontSize;
  const upm = shaper.unitsPerEm(fontRef) || 1000;
  let firstLineYMax = -Infinity;
  for (const seg of segsFirstLine) {
    for (const ch of seg.text) {
      const ym = shaper.glyphYMax(fontRef, ch);
      if (ym > shaper.glyphYMin(fontRef, ch) && ym > firstLineYMax) firstLineYMax = ym;
    }
  }

  const firstLineAscentPx = (firstLineYMax / upm) * fontSize;
  // Ink-top-anchored rects start at actual ink, including accents above caps and ink below the baseline.
  const delta = capHeightPx - firstLineAscentPx;
  const ascenderDeltaPx = Number.isFinite(firstLineAscentPx)
    ? inkTopAnchored
      ? delta
      : firstLineAscentPx > 0
        ? Math.max(0, delta)
        : 0
    : 0;
  return { capHeightPx, ascenderDeltaPx };
}

/**
 * First-line cap metrics for a box's inline-edit caret overlay (screen/book px at `box.fontSize`). Mirrors the
 * renderer's first-line setup — same `firstLineCapMetrics`, same fontCaps segmentation — so the caret's transparent
 * textarea sits exactly where the engine draws the glyphs, across normal / all-caps / small-caps text.
 */
/**
 * PSD point text whose rect starts at its ink top: the parser pins it (`firstBaselineOffset`, which the Rust engine
 * seats exactly and this layout approximates by the ink) or marks it `firstBaselineRule: 'ink'`. Every other box
 * keeps the cap clamp it was stored under.
 */
function isInkTopAnchored(box: Pick<RenderTextBox, 'textMode' | 'firstBaselineOffset' | 'firstBaselineRule'>): boolean {
  return box.textMode !== 'block' && (box.firstBaselineOffset !== undefined || box.firstBaselineRule === 'ink');
}

export function capMetricsForCaret(box: RenderTextBox, shaper: Shaper, fontRef: string): FirstLineCapMetrics {
  // The renderer measures the first WRAPPED line (each run in its own case): the caret takes that line too.
  const plan = planTextLayout(box, shaper, fontRef);
  if (plan) return firstLineCapMetrics(plan.lines[0]?.segs ?? [], shaper, fontRef, plan.fontSize, isInkTopAnchored(box));
  const fontSize = baselineFontSize(box);
  const smallCapSize = box.smallCapSize ?? PSD_SMALL_CAP_SIZE_DEFAULT;
  // From the paragraph rule a soft return ends the first line too.
  const breaks = textLayoutAtLeast(box.textLayout, TEXT_LAYOUT_PARAGRAPHS) ? PARAGRAPH_OR_SOFT_BREAK : '\n';
  const firstRawLine = (box.text ?? '').split(breaks)[0] ?? '';
  // No run cases here: the first line is measured in the box case the layout draws.
  const caps = boxCaseTheLayoutDraws(box);
  const segs = applyFontCapsSegments(firstRawLine, caps, smallCapSize, undefined);
  return firstLineCapMetrics(segs, shaper, fontRef, fontSize, isInkTopAnchored(box));
}

const IDENTITY: readonly [number, number, number, number, number, number] = [1, 0, 0, 1, 0, 0];

/** Раздвигается обычный пробел; NBSP/узкие пробелы держат ширину, пока шрифт даёт им СВОЙ глиф. */
const JUSTIFY_SPACE = ' ';

/**
 * Выключка (`align: 'justify'`): добавка к КАЖДОМУ межсловному пробелу строки, чтобы её правый край
 * дошёл до края контейнера. `0` = строку не выключаем.
 *
 * Контейнер — полоса контура в area-режиме и рамка бокса в обычном block: Photoshop выключает по
 * тому же интервалу, по которому переносит, поэтому в area-тексте строки прижимаются к ОБЕИМ
 * границам фигуры (левый край даёт лесенку контура, правый — флэш), а не к прямоугольнику.
 *
 * Хвостовые пробелы висят за краем (PS их тоже не считает), поэтому ширина меряется по видимой
 * части строки. Строка, влезшая только за счёт запаса правила или сжатия пробелов ({@link WrapRule}),
 * подтягивается назад (отрицательная добавка); всё, что шире — неразрывный токен, его пробелы не жмём.
 */
function justifyDeltaPerSpace(
  line: WrappedLine,
  containerWidth: number,
  measureLine: (text: string) => number,
  isGap: (ch: string) => boolean,
  rule: WrapRule,
): number {
  if (isForcedLineEnd(line) || !(containerWidth > 0)) return 0;
  const visible = visibleLineText(line.text);
  // Считаем ровно те символы, которые отрисовка потом и раздвинет: посчитать один набор, а
  // раздвинуть другой — значит промахнуться мимо края контейнера на разницу их количеств.
  let gaps = 0;
  for (const ch of visible) if (isGap(ch)) gaps += 1;
  if (gaps === 0) return 0;
  const extra = containerWidth - measureLine(visible);
  if (extra < -(rule.slack + rule.squeezePerGap * gaps)) return 0;
  return extra / gaps;
}

/**
 * Перенос по правилу бокса (зеркало Rust `WrapRule`): `slack` — запас к рамке, `squeezePerGap` — сколько px отдаёт
 * каждый пробел выключенной строки, чтобы она осталась на своей строке (Photoshop сжимает до минимума Word Spacing).
 */
interface WrapRule {
  slack: number;
  squeezePerGap: number;
}

/**
 * `spaceAdvance` зовётся только для выключенного бокса с `textLayout`: legacy его не шейпит. `justified` — some line
 * of the box justifies (the box align, or a paragraph's from `TEXT_LAYOUT_PARAGRAPHS`).
 */
function wrapRuleOf(box: RenderTextBox, justified: boolean, spaceAdvance: () => number): WrapRule {
  const ratio = justifySqueezeRatio(box.textLayout);
  return { slack: wrapSlackPx(box.textLayout), squeezePerGap: ratio > 0 && justified ? ratio * spaceAdvance() : 0 };
}

/**
 * Baseline of each line under the first one's seat (`lineHeightPx`) — mirror of Rust `line_baselines`. Legacy boxes and
 * area contours (bands cut on one pitch) step by one leading; from `TEXT_LAYOUT_LINE_STACK` the step into line i is its
 * own `leadingOf(i)` plus the space after/before when line i opens a paragraph — none above the first one.
 */
function lineBaselines(
  box: RenderTextBox,
  lines: readonly LineParagraph[],
  lineHeightPx: number,
  shaped: boolean,
  leadingOf: (i: number) => number,
): number[] {
  if (shaped || !textLayoutAtLeast(box.textLayout, TEXT_LAYOUT_LINE_STACK)) return lines.map((_, i) => lineHeightPx * (i + 1));
  let baseline = lineHeightPx;
  return lines.map((line, i) => {
    if (i > 0) baseline += leadingOf(i) + line.gapBefore;
    return baseline;
  });
}

/** The box steps its lines off one leading: paragraph spacing or a run's own leading under `TEXT_LAYOUT_LINE_STACK`. */
export function stacksUnevenLines(box: RenderTextBox): boolean {
  if (!textLayoutAtLeast(box.textLayout, TEXT_LAYOUT_LINE_STACK)) return false;
  const gap = (box.spaceAfter ?? 0) + (box.spaceBefore ?? 0);
  const paragraphGap =
    textLayoutAtLeast(box.textLayout, TEXT_LAYOUT_PARAGRAPHS) &&
    (box.paragraphRuns ?? []).some(p => !!p.spaceAfter || !!p.spaceBefore);
  return (
    gap !== 0 ||
    paragraphGap ||
    (box.runs ?? []).some(r => r.lineHeightPx !== undefined && r.lineHeightPx > 0 && r.lineHeightPx !== box.lineHeight)
  );
}

/** Leading of line `i` of multi-run text: the largest of its runs; an empty line takes the run of its line break. */
function runLineLeading(spans: readonly RunSpan[], lineCharStart: number, runs: TextRun[], lineHeightPx: number): number {
  const of = (run: TextRun): number => (run.lineHeightPx !== undefined && run.lineHeightPx > 0 ? run.lineHeightPx : lineHeightPx);
  if (spans.length === 0) return of(findRunForChar(lineCharStart, runs));
  return spans.reduce((max, seg) => Math.max(max, of(seg.run)), 0);
}

/**
 * Часть строки, которую меряет выравнивание (зеркало Rust `aligned_line_text`). Photoshop кончает строку point-текста
 * на пере, хвостовые пробелы включительно (растры PS с right/center); area-текст и legacy-боксы вешают их за край.
 */
function alignedLineText(box: RenderTextBox, line: string): string {
  return box.textMode !== 'block' && textLayoutAtLeast(box.textLayout, TEXT_LAYOUT_PEN_LINES) ? line : visibleLineText(line);
}

/** Мера строки для решения о переносе: ширина минус то, что пробелы выключки могут отдать. */
function breakMeasure(
  rule: WrapRule,
  measureLine: (line: string) => number,
  isGap: (ch: string) => boolean,
): (line: string) => number {
  if (rule.squeezePerGap === 0) return measureLine;
  return line => {
    let gaps = 0;
    for (const ch of line) if (isGap(ch)) gaps += 1;
    return measureLine(line) - rule.squeezePerGap * gaps;
  };
}

/**
 * Добавка выключки на каждую строку абзаца; `undefined` = выключки нет (обычный align).
 * Выключка живёт только в block: у point-текста рамки переноса нет, `justify` там = `left`.
 *
 * `measureLine` обязана мерить строку ТОЙ ЖЕ раскладкой, которой потом расставляются глифы:
 * добавка считается как «сколько не хватает до края», и любое расхождение меры с раскладкой
 * уезжает во флэш один в один (multi-run путь передаёт сюда per-run меру — см. FKS-11074).
 */
function justifyDeltasForLines(
  box: RenderTextBox,
  paragraphs: TextParagraphs,
  wrapped: readonly WrappedLine[],
  lineParagraphs: readonly LineParagraph[],
  areaSpanAt: (lineIndex: number) => AreaLineSpan | null,
  measureLine: (text: string, lineIndex: number) => number,
  isGap: (ch: string) => boolean,
  rule: WrapRule,
): number[] | undefined {
  if (!paragraphs.anyJustified()) return undefined;
  return wrapped.map((line, i) => {
    const lp = lineParagraphs[i];
    if (!lp.justified) return 0;
    // Ниже контура строка переносится по rect-фолбэку — по нему же и выключается.
    const span = areaSpanAt(i);
    return justifyDeltaPerSpace(
      line,
      (span ? span.width : box.rect.width) - lp.frameReduction,
      visible => measureLine(visible, i),
      isGap,
      rule,
    );
  });
}

/**
 * Рамка выравнивания строки. В area-режиме это полоса контура — в том числе для строк НИЖЕ контура:
 * они переносятся по rect-фолбэку, поэтому и равняются по rect, а не по content-ширине абзаца.
 *
 * FKS-11074: раньше здесь стоял `max(span.width, adv)`, и строка шире своей полосы молча теряла
 * выравнивание (`aligned` = 0) — рисовалась как left при любом align. Полоса контура — единственная
 * рамка строки: не влезший неразрывный токен свисает влево ровно так же, как свисал бы вправо при left.
 */
function alignWidthForLine(span: AreaLineSpan | null, shaped: boolean, rectWidth: number, contentAlignWidth: number): number {
  if (span) return span.width;
  return shaped ? rectWidth : contentAlignWidth;
}

// Layout follows the backend/print recipe (run-full-cover-parity.cjs). Lines come from explicit `\n`
// plus word-wrap in block mode (svg-text-generator parity); each line gets its own baseline.
// Photoshop super/subscript: shrink the font and shift the baseline (default size 0.583, offset 0.333).
function baselineFontSize(box: RenderTextBox): number {
  if (box.fontBaseline === 1) return box.fontSize * (box.superscriptSize ?? PSD_SUPERSCRIPT_SIZE_DEFAULT);
  if (box.fontBaseline === 2) return box.fontSize * (box.subscriptSize ?? PSD_SUBSCRIPT_SIZE_DEFAULT);
  return box.fontSize;
}

function baselineShiftPx(box: RenderTextBox): number {
  if (box.fontBaseline === 1) return -box.fontSize * (box.superscriptPosition ?? PSD_SUPERSCRIPT_POSITION_DEFAULT);
  if (box.fontBaseline === 2) return box.fontSize * (box.subscriptPosition ?? PSD_SUBSCRIPT_POSITION_DEFAULT);
  return 0;
}

function resolvePathStartArc(
  textPath: NonNullable<RenderTextBox['textPath']>,
  align: RenderTextBox['align'],
  totalLength: number,
  naturalTotal: number,
): number {
  const { pathRangeStart, pathRangeEnd } = textPath;
  if (typeof pathRangeStart === 'number' && typeof pathRangeEnd === 'number') {
    const start = pathRangeStart * totalLength;
    const end = pathRangeEnd * totalLength;
    const rangeArc = end >= start ? end - start : end + totalLength - start;
    if (align === 'left') return start;
    if (align === 'right') return start + rangeArc - naturalTotal;
    return start + rangeArc / 2 - naturalTotal / 2;
  }

  return (textPath.startOffset ?? 0) * totalLength;
}

export interface PathGlyphPlacement {
  /** UTF-16 span of the source character; an all-caps expansion (ß → SS) repeats it on every glyph. */
  charStart: number;
  charEnd: number;
  glyphId: number;
  /** Baseline start — half an advance back from the curve point, not on the curve itself. */
  x: number;
  y: number;
  /** Tangent under the mid-advance, turned half a circle on a flipped path. */
  angle: number;
  advance: number;
}

export interface PathGlyphPlan {
  glyphs: PathGlyphPlacement[];
  /** Where the first glyph starts — an emptied text keeps its caret here. */
  home: { x: number; y: number; angle: number };
  fontSize: number;
  hScale: number;
  vScale: number;
}

// Glyphs ride the curve (path-distribution parity); the drawn glyphs and the edit caret share this one plan.
export function planGlyphsOnPath(box: RenderTextBox, shaper: Shaper, fontRef: string): PathGlyphPlan | undefined {
  const tp = box.textPath;
  if (!tp?.d) return undefined;
  const sampler = buildPathSampler(tp.d, tp.closed === true);
  const total = sampler.totalLength;
  if (total === 0) return undefined;

  const fontSize = box.fontSize;
  const hScale = box.horizontalScale ?? 1;
  const vScale = box.verticalScale ?? 1;
  const trackingPx = ((box.tracking ?? 0) / 1000) * fontSize;
  const caps = (boxCaseTheLayoutDraws(box) ?? 0) >= 2;
  // Per source character keeps the caret's offsets; toUpperCase has no context rules, so the run is unchanged.
  const chars: Array<{ ch: string; charStart: number; charEnd: number }> = [];
  // From the paragraph rule a soft return stands where the import used to write `\n`: the path takes it as one.
  const soft = textLayoutAtLeast(box.textLayout, TEXT_LAYOUT_PARAGRAPHS);
  let offset = 0;
  for (const typed of box.text ?? '') {
    const source = soft && typed === SOFT_LINE_BREAK ? '\n' : typed;
    for (const ch of caps ? source.toUpperCase() : source) chars.push({ ch, charStart: offset, charEnd: offset + source.length });
    offset += source.length;
  }

  const opts: ShaperOpts = {};
  const advances = chars.map(({ ch }) => shaper.measureAdvance(fontRef, ch, fontSize, opts) * hScale + trackingPx);
  const naturalTotal = advances.reduce((sum, a) => sum + a, 0);
  const flip = tp.flip === true;
  let cursor = ((resolvePathStartArc(tp, box.align, total, naturalTotal) % total) + total) % total;
  const start = sampler.pointAtLength(cursor);
  const home = { x: start.x, y: start.y, angle: flip ? start.tangent + Math.PI : start.tangent };

  const glyphs: PathGlyphPlacement[] = [];
  for (let i = 0; i < chars.length; i++) {
    const adv = advances[i];
    let centerOnPath = cursor + adv / 2;
    if (tp.closed) {
      centerOnPath = ((centerOnPath % total) + total) % total;
    } else if (centerOnPath > total || centerOnPath < 0) {
      break;
    }

    const { x, y, tangent } = sampler.pointAtLength(centerOnPath);
    const angle = flip ? tangent + Math.PI : tangent;
    // origin so the glyph's mid-advance lands on the curve point once rotated by `angle`
    glyphs.push({
      charStart: chars[i].charStart,
      charEnd: chars[i].charEnd,
      glyphId: shaper.charGlyphId(fontRef, chars[i].ch),
      x: x - (adv / 2) * Math.cos(angle),
      y: y - (adv / 2) * Math.sin(angle),
      angle,
      advance: adv,
    });
    cursor += flip ? -adv : adv;
  }

  return { glyphs, home, fontSize, hScale, vScale };
}

function layoutGlyphsOnPath(box: RenderTextBox, shaper: Shaper, fontRef: string): LaidOutGlyph[] {
  const plan = planGlyphsOnPath(box, shaper, fontRef);
  if (!plan) return [];
  // A tab keeps its advance (and caret slot) but draws nothing, as on the horizontal paths.
  const text = box.text ?? '';
  return plan.glyphs
    .filter(g => text[g.charStart] !== '\t')
    .map(g => ({
      fontRef,
      glyphId: g.glyphId,
      x: g.x,
      y: g.y,
      scaleX: plan.hScale,
      scaleY: plan.vScale,
      fontSize: plan.fontSize,
      rotation: g.angle,
    }));
}

/** Returns the run covering `charIdx`; falls back to the last run. */
function findRunForChar(charIdx: number, runs: TextRun[]): TextRun {
  for (const r of runs) {
    if (charIdx >= r.from && charIdx < r.to) return r;
  }

  return runs[runs.length - 1];
}

// wrapLines may trimStart() leading whitespace from a wrapped sub-line; map each line back to its
// original-text char start so runs (indexed in original coords) align to the wrapped lines.
// `soft` (by paragraphs): a soft return is a line separator too.
function computeLineCharStarts(original: string, lines: readonly WrappedLine[], soft: boolean): number[] {
  const starts: number[] = [];
  let oc = 0;
  for (let li = 0; li < lines.length; li++) {
    const line = lines[li].text;
    if (li > 0 && oc < original.length && (original[oc] === '\n' || (soft && original[oc] === SOFT_LINE_BREAK))) oc++;
    while (oc < original.length && line.length > 0 && original[oc] !== line[0] && original[oc] === ' ') oc++;
    starts.push(oc);
    // Дефис переноса по слогам дописан раскладкой — в исходном тексте его нет.
    oc += line.length - (lines[li].hyphenated ? 1 : 0);
  }

  return starts;
}

/** Правила переноса по слогам из абзацного стиля PSD; `undefined` — переносы выключены. Зеркало Rust `hyphenation_rules`. */
export function hyphenationRulesOf(box: RenderTextBox): HyphenationRules | undefined {
  if (box.autoHyphenate !== true) return undefined;
  const d = DEFAULT_HYPHENATION_RULES;
  const count = (v: number | undefined, fallback: number): number => (v == null ? fallback : Math.max(0, Math.round(v)));
  return {
    minWordLength: count(box.hyphenatedWordSize, d.minWordLength),
    minPrefix: count(box.preHyphen, d.minPrefix),
    minSuffix: count(box.postHyphen, d.minSuffix),
    maxConsecutive: count(box.consecutiveHyphens, d.maxConsecutive),
  };
}

/** Per-char hyphenation language of a box the import stamped with `language`, from `textLayout` 6. Rust twin `word_languages`. */
export function wordLanguagesOf(box: RenderTextBox, text: string): WordLanguages | undefined {
  if (box.language == null || !textLayoutAtLeast(box.textLayout, TEXT_LAYOUT_RUN_LANGUAGE)) return undefined;
  return new WordLanguages(text, box.language, box.runs);
}

/**
 * Таб у шрифта — `.notdef`: ширину Photoshop ему оставляет, квадрат не рисует. Зеркало Rust `tab_clusters`:
 * прячется ровно глиф таба, остальные `.notdef` строки остаются видны, как в печати. Без `cluster` (лигатуры
 * у запасного шейпера) глиф таба от прочих не отличить — тогда не прячем ничего: квадрат лучше пропавшей буквы.
 */
function isHiddenTabGlyph(segText: string, glyph: RunGlyph): boolean {
  return glyph.cluster !== undefined && segText[glyph.cluster] === '\t';
}

interface RunSpan {
  text: string;
  fontSizeScale: number;
  run: TextRun;
}

function runFontCaps(run: TextRun, boxFontCaps: number | undefined): number | undefined {
  return run.fontCaps ?? boxFontCaps;
}

/** A char drawn in another case than the box's, which the textarea takes; a char past every run takes the last run. */
export function runCapsDivergeFromBox(box: Pick<RenderTextBox, 'text' | 'runs' | 'fontCaps'>): boolean {
  const runs = box.runs;
  const text = box.text ?? '';
  if (!runs?.length || text.length === 0) return false;
  const capsAt = (i: number): number => runFontCaps(findRunForChar(i, runs), box.fontCaps) ?? 0;
  const boxCaps = box.fontCaps ?? 0;
  for (let i = 0; i < text.length; i++) if (capsAt(i) !== boxCaps) return true;
  return false;
}

// Split a line into run-aware caps-segments; with one whole-text run this equals the single-run segments.
// A hyphenated line's trailing hyphen has no source char: it takes the run of the letter before it (Rust twin).
function buildRunSpans(
  lineText: string,
  lineCharStart: number,
  hyphenated: boolean,
  runs: TextRun[],
  fontCaps: number | undefined,
  smallCapSize: number,
): RunSpan[] {
  if (lineText.length === 0) return [];

  const result: RunSpan[] = [];
  let cursor = 0;
  while (cursor < lineText.length) {
    const run = findRunForChar(lineCharStart + cursor, runs);
    const runEndInLine = run.to - lineCharStart;
    let spanEnd = Math.min(lineText.length, runEndInLine > cursor ? runEndInLine : lineText.length);
    if (hyphenated && spanEnd === lineText.length - 1) spanEnd = lineText.length;
    const spanText = lineText.slice(cursor, spanEnd);
    for (const seg of applyFontCapsSegments(spanText, runFontCaps(run, fontCaps), smallCapSize, undefined)) {
      if (seg.text.length > 0) result.push({ text: seg.text, fontSizeScale: seg.fontSizeScale, run });
    }

    cursor = spanEnd;
  }

  return result;
}

function isRunSpan(seg: CapsSegment): seg is RunSpan {
  return 'run' in seg;
}

/**
 * Photoshop's vertical scale stretches the glyphs about their baseline and leaves the line grid alone: the leading
 * keeps its value, and an area frame keeps its first baseline a full cap height under its top. A point text's rect
 * starts at the ink top the PSD reports — already scaled — so there the first baseline comes up by the part of the
 * ascent the scale took away. `firstLineAscentPx` is the unscaled ascent the layout seats the first baseline by
 * (`capHeight − ascenderDelta`). Mirror of Rust `first_baseline_lift`.
 */
function firstBaselineLift(box: RenderTextBox, firstLineAscentPx: number, vScale: number): number {
  return box.textMode === 'block' ? 0 : firstLineAscentPx * (1 - vScale);
}

/** A shaped glyph's y with only its own offset from the baseline (marks) scaled — the baseline itself stays put. */
function aboutBaseline(gy: number, baseline: number, vScale: number): number {
  return vScale === 1 ? gy : baseline + (gy - baseline) * vScale;
}

/** The typed size optical kerning works at, when a text of `fontSize` asks for it (`kerning` -1). Rust `optical_kern`. */
function opticalKern(box: RenderTextBox, optical: boolean, fontSize: number): number | undefined {
  if (!optical || !textLayoutAtLeast(box.textLayout, TEXT_LAYOUT_OPTICAL_KERN)) return undefined;
  return fontSize / ((box.transform && textMatrixScale(box.transform)) || 1);
}

function resolveRunOpts(run: TextRun, box: RenderTextBox): FontLayoutOptions {
  const runIsOptical = run.kerning === -1;
  const runAutoKerning = runIsOptical ? true : (run.autoKerning ?? box.autoKerning) !== false;
  const runExplicitKerning =
    (run.autoKerning ?? box.autoKerning) === false && run.kerning != null && !runIsOptical ? run.kerning / 1000 : 0;
  const runTracking = run.tracking ?? box.tracking ?? 0;
  const features: Record<string, boolean> = {};
  const lig = run.ligatures ?? box.ligatures;
  const dlig = run.dLigatures ?? box.dLigatures;
  if (lig === false) features['liga'] = false;
  if (dlig === true) features['dlig'] = true;
  return {
    letterSpacing: runTracking / 1000 + runExplicitKerning + fauxBoldAdvanceEm(box),
    kerning: runAutoKerning,
    features,
    opticalKern: opticalKern(box, runIsOptical, resolveRunFontSize(run, box)),
  };
}

function resolveRunFontSize(run: TextRun, box: RenderTextBox): number {
  const base = run.fontSize ?? box.fontSize;
  const fb = run.fontBaseline ?? box.fontBaseline;
  if (fb === 1) return base * (box.superscriptSize ?? PSD_SUPERSCRIPT_SIZE_DEFAULT);
  if (fb === 2) return base * (box.subscriptSize ?? PSD_SUBSCRIPT_SIZE_DEFAULT);
  return base;
}

function resolveRunBaseShift(run: TextRun, box: RenderTextBox): number {
  const base = run.fontSize ?? box.fontSize;
  const fb = run.fontBaseline ?? box.fontBaseline;
  const explicit = -(run.baselineShift ?? 0);
  if (fb === 1) return explicit - base * (box.superscriptPosition ?? PSD_SUPERSCRIPT_POSITION_DEFAULT);
  if (fb === 2) return explicit + base * (box.subscriptPosition ?? PSD_SUBSCRIPT_POSITION_DEFAULT);
  return explicit;
}

const VERTICAL_UPRIGHT_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x1100, 0x11ff],
  [0x2e80, 0x2fdf],
  [0x3000, 0x30ff],
  [0x3400, 0x4dbf],
  [0x4e00, 0x9fff],
  [0xac00, 0xd7af],
  [0xf900, 0xfaff],
  [0xff00, 0xffef],
];

function isVerticalUpright(ch: string): boolean {
  const cp = ch.codePointAt(0) ?? 0;
  return VERTICAL_UPRIGHT_RANGES.some(([lo, hi]) => cp >= lo && cp <= hi);
}

// MVP: latin/digits rotate 90° (full tate-chu-yoko / UTR#50 deferred); separate path leaves horizontal layout untouched.
function layoutGlyphsVertical(box: RenderTextBox, shaper: Shaper, fontRef: string): LaidOutGlyph[] {
  const text = box.text ?? '';
  if (!text) return [];

  const fontSize = box.fontSize;
  const hScale = box.horizontalScale ?? 1;
  const vScale = box.verticalScale ?? 1;
  const trackingPx = ((box.tracking ?? 0) / 1000) * fontSize;
  const ascentPx = resolveCapHeightRatio(shaper, fontRef) * fontSize;

  const isOpticalKerning = box.kerning === -1;
  const useAutoKerning = isOpticalKerning ? true : box.autoKerning !== false;
  const otFeatures: Record<string, boolean> = {};
  if (box.ligatures === false) otFeatures['liga'] = false;
  if (box.dLigatures === true) otFeatures['dlig'] = true;
  const baseOpts: ShaperOpts = {
    letterSpacing: (box.tracking ?? 0) / 1000,
    kerning: useAutoKerning,
    features: otFeatures,
  };
  const vOpts: ShaperOpts = { ...baseOpts, vertical: true };
  const hOpts: ShaperOpts = { ...baseOpts, vertical: false };

  const [scaleX, scaleY] = textMatrixAnisotropy(box.transform ?? IDENTITY);

  const canvasLeft = Math.round(box.rect.x);
  const canvasTop = Math.round(box.rect.y);
  const columnWidth = fontSize * vScale;
  const wrapHeight = box.textMode === 'block' ? box.rect.height : Infinity;

  interface Cell {
    ch: string;
    rotated: boolean;
    col: number;
    cellTop: number;
  }

  const cells: Cell[] = [];
  const columnUsed: number[] = [];
  let col = 0;
  let penY = 0;
  // From the paragraph rule a soft return opens a column too, as the `\n` the import used to write for it.
  const soft = textLayoutAtLeast(box.textLayout, TEXT_LAYOUT_PARAGRAPHS);
  for (const ch of text) {
    if (ch === '\n' || (soft && ch === SOFT_LINE_BREAK)) {
      columnUsed[col] = penY;
      col += 1;
      penY = 0;
      continue;
    }

    const rotated = !isVerticalUpright(ch);
    const advance = shaper.measureAdvance(fontRef, ch, fontSize, rotated ? hOpts : vOpts) + trackingPx;
    if (penY > 0 && penY + advance > wrapHeight) {
      columnUsed[col] = penY;
      col += 1;
      penY = 0;
    }

    cells.push({ ch, rotated, col, cellTop: penY });
    penY += advance;
  }

  columnUsed[col] = penY;
  const numColumns = col + 1;

  const totalWidth = numColumns * columnWidth;
  const blockShift =
    box.align === 'center' ? (box.rect.width - totalWidth) / 2 : box.align === 'right' ? box.rect.width - totalWidth : 0;
  const vAlign = resolveVerticalAlign(box.verticalAlign);

  const out: LaidOutGlyph[] = [];
  for (const cell of cells) {
    // A tab keeps its cell but draws nothing, as on the horizontal paths.
    if (cell.ch === '\t') continue;
    const columnLeft = canvasLeft + blockShift + totalWidth - (cell.col + 1) * columnWidth;
    const used = columnUsed[cell.col] ?? 0;
    const vOffset = vAlign === 'center' ? (wrapHeight - used) / 2 : vAlign === 'bottom' ? wrapHeight - used : 0;
    const cellTopY = canvasTop + vOffset + cell.cellTop;
    const glyphId = shaper.layoutRun(fontRef, cell.ch, fontSize, 0, cell.rotated ? hOpts : vOpts)[0]?.glyphId ?? 0;

    if (cell.rotated) {
      out.push({
        fontRef,
        glyphId,
        x: columnLeft + (columnWidth + ascentPx) / 2,
        y: cellTopY,
        scaleX: scaleX * hScale,
        scaleY: scaleY * vScale,
        fontSize,
        rotation: Math.PI / 2,
      });
    } else {
      out.push({
        fontRef,
        glyphId,
        x: columnLeft,
        y: cellTopY + ascentPx,
        scaleX: scaleX * hScale,
        scaleY: scaleY * vScale,
        fontSize,
      });
    }
  }

  return out;
}

/**
 * How far a box's laid-out text spills past its frame, in the renderer's own units; `<= 0` on an axis means that
 * axis fits. Both numbers fall out of the SAME pass that positions the drawn glyphs — `heightOver` is the ink
 * height the vertical align distributes against, `widthOver` the content width the horizontal align uses — so a
 * consumer gating on them cannot drift from the canvas the way an independently re-measured extent does.
 */
export interface TextFrameFit {
  /** Ink height − rect height, in canvas px. */
  heightOver: number;
  /** Free (point) text: longest line − rect width, in advance space. Block text: the widest line's overhang past the
   *  wrap budget it was laid against — normally ≤ 0, positive only where the wrap could not honour the budget. */
  widthOver: number;
  /** Lines the text actually laid out into — authored breaks plus, for block text, the wrap. `0` = never measured. */
  lines: number;
}

/** Optional layout side-channel: `overflow` = the wrapped text's ink height exceeds the box rect height. */
export interface TextLayoutMetrics {
  overflow: boolean;
  /** Both frame axes. Filled by the horizontal paths only — path-bent and vertical text lay out on their own. */
  fit?: TextFrameFit;
}

// Both horizontal layout paths report the frame the same way. Only point text consults `alignWidth`, and there it is
// already the rect divided by the residual scaleX the draw step re-applies — the same space the line advances live
// in, so the width comparison never mixes units.
function fillMetrics(
  metrics: TextLayoutMetrics,
  box: RenderTextBox,
  inkHeight: number,
  contentWidth: number,
  alignWidth: number,
  blockSpill: number,
  lines: number,
): void {
  const fit: TextFrameFit = {
    heightOver: inkHeight - box.rect.height,
    widthOver: box.textMode === 'block' ? blockSpill : contentWidth - alignWidth,
    lines,
  };
  metrics.overflow = fit.heightOver > 0;
  metrics.fit = fit;
}

/**
 * Block text wraps to its frame, so its lines normally end inside the budget and the spill is ≤ 0. "Normally" is not
 * "always": the break-word fallback splits a too-long token character by character and cannot split further, so a
 * single glyph wider than the budget stays on its line and overhangs the frame. Measuring the real overhang keeps
 * `widthOver` from claiming a fit the renderer does not produce.
 */
function blockWidthSpill(box: RenderTextBox, lineAdvances: readonly number[], budgetAt: (i: number) => number): number {
  if (box.textMode !== 'block' || !lineAdvances.length) return 0;
  let spill = Number.NEGATIVE_INFINITY;
  for (let i = 0; i < lineAdvances.length; i++) spill = Math.max(spill, (lineAdvances[i] ?? 0) - budgetAt(i));
  return Number.isFinite(spill) ? spill : 0;
}

/**
 * Frame fit for a candidate string, measured by the renderer's own layout pass.
 *
 * The inline-edit clamp's SSoT. Re-deriving "does this text fit" from the edit overlay's DOM box diverges from the
 * drawn result on every axis the engine treats specially: block text wraps at `rect.width` plus its rule's slack
 * and is measured by glyph ink minus the first line's leading (not by CSS line boxes); free text's height is its ink,
 * not a rounded line count; super/subscript shrinks both the font size and the line height. Routing the decision
 * through `layoutGlyphsBackend` makes all of that automatic instead of mirrored.
 *
 * Path-bent and vertical text lay out through branches that fill no metrics and report a fit — the caller excludes
 * them anyway, since the rectangular edit overlay cannot represent either layout.
 */
export function measureTextFrameFit(box: RenderTextBox, text: string, shaper: Shaper, fontRef: string): TextFrameFit {
  const metrics: TextLayoutMetrics = { overflow: false };
  layoutGlyphsBackend({ ...box, text }, shaper, fontRef, metrics);
  return metrics.fit ?? { heightOver: 0, widthOver: 0, lines: 0 };
}

export interface CapsSegment {
  text: string;
  fontSizeScale: number;
}

/**
 * Метрики и геометрия бокса, из которых растут И перенос, И отрисовка глифов, И каретка инлайн-редактора.
 * Держится одной функцией намеренно: каретка обязана видеть ровно ту сетку строк, по которой движок рисует
 * (FKS-11074 — area-текст переносится по полосам контура, а браузерная textarea по прямоугольнику).
 */
interface TextBoxGeometry {
  fontSize: number;
  baseShift: number;
  lineHeightPx: number;
  capHeightPx: number;
  yGap: number;
  opts: ShaperOpts;
  hScale: number;
  vScale: number;
  scaleX: number;
  scaleY: number;
  smallCapSize: number;
  roundAdvance: boolean;
  canvasLeft: number;
  canvasTop: number;
  areaSpans: (AreaLineSpan | null)[] | undefined;
  areaSpanAt: (i: number) => AreaLineSpan | null;
  indentReduction: number;
  startIndent: number;
  firstLineIndent: number;
  wrapWidth: number;
  shapedWidthAt: (i: number) => number;
  wrapRule: WrapRule;
  /** Мера строки для решения о переносе (выключка с `textLayout` вычитает сжатие пробелов). */
  measureBreakAdvance: (line: string) => number;
  segAdvance: (seg: CapsSegment) => number;
  lineAdvanceOfSegs: (segs: readonly CapsSegment[]) => number;
  measureLineAdvance: (line: string) => number;
  measurePrefixAdvance: (line: string) => number;
  /** Глиф пробела выключки; `-1` — совпасть с ним не может ни один настоящий глиф. */
  spaceGlyphId: number;
  isJustifyGap: (ch: string) => boolean;
  /** The box's paragraphs: one set of attributes below `TEXT_LAYOUT_PARAGRAPHS`, each its own from it. */
  paragraphs: TextParagraphs;
}

function textBoxGeometry(box: RenderTextBox, shaper: Shaper, fontRef: string): TextBoxGeometry {
  const fontSize = baselineFontSize(box);
  const baseShift = baselineShiftPx(box);
  // super/subscript shrinks the font; keep line spacing proportional to it (matches the editor).
  const lineHeightPx = box.lineHeight * (fontSize / box.fontSize);
  const capHeightPx = resolveCapHeightRatio(shaper, fontRef) * fontSize;
  const yGap = lineHeightPx - capHeightPx;
  const baseLsRatio = (box.tracking ?? 0) / 1000;
  // kerning=-1 is Photoshop's optical sentinel; opentype.js has no optical mode -> fall back to metrics (auto) kerning.
  const isOpticalKerning = box.kerning === -1;
  const useAutoKerning = isOpticalKerning ? true : box.autoKerning !== false;
  // explicit per-pair kerning (1/1000 em) folds into letter-spacing when autoKerning is off (svg-text-generator parity).
  const explicitKerningEm = box.autoKerning === false && box.kerning != null && !isOpticalKerning ? box.kerning / 1000 : 0;
  const letterSpacingRatio = baseLsRatio + explicitKerningEm + fauxBoldAdvanceEm(box);
  // opentype.js GSUB toggles: liga defaults on for latn (disable when ligatures===false); dlig is opt-in.
  const otFeatures: Record<string, boolean> = {};
  if (box.ligatures === false) otFeatures['liga'] = false;
  if (box.dLigatures === true) otFeatures['dlig'] = true;
  // Photoshop rounds advance to a whole pixel per segment when fractional widths are off (precise wrap parity).
  const roundAdvance = box.useFractionalGlyphWidths === false;
  // PSD per-glyph stretch (Type Options "Масштаб глифов"); composes with the matrix anisotropy below.
  const hScale = box.horizontalScale ?? 1;
  const vScale = box.verticalScale ?? 1;
  const smallCapSize = box.smallCapSize ?? PSD_SMALL_CAP_SIZE_DEFAULT;

  // fontSize already carries the matrix mean scale; apply only residual anisotropy (backend text.generator normalization).
  const [scaleX, scaleY] = textMatrixAnisotropy(box.transform ?? IDENTITY);

  const opts: ShaperOpts = {
    letterSpacing: letterSpacingRatio,
    kerning: useAutoKerning,
    features: otFeatures,
    opticalKern: opticalKern(box, isOpticalKerning, box.fontSize),
  };

  // Area Type on Shape: пер-строчные интервалы контура. Ниже контура (индекс за пределами
  // массива) — обычный rect-wrap, чтобы переполнение оставалось видимым, а не терялось.
  // capHeightPx — та же величина, что задаёт базлайн ниже (`baseline − yGap` = bandTop + capHeightPx),
  // поэтому сэмплы контура ложатся ровно на строки, где реально стоят глифы.
  const builtAreaSpans =
    box.textMode === 'block' && box.areaShape?.d
      ? buildAreaShapeLineSpans(box.areaShape.d, box.rect.width, box.rect.height, lineHeightPx, capHeightPx)
      : undefined;
  // Пустой список = контур непригоден (мусорный d / вырожденная геометрия / нет живых полос):
  // shaped-режим не включаем, иначе молча потеряли бы verticalAlign и rect-guard переноса.
  const areaSpans: (AreaLineSpan | null)[] | undefined =
    builtAreaSpans !== undefined && builtAreaSpans.length > 0 ? builtAreaSpans : undefined;
  const areaSpanAt = (i: number): AreaLineSpan | null => (areaSpans === undefined || i >= areaSpans.length ? null : areaSpans[i]);

  // Block (PS area text) wraps to its frame; free (PS point text) has no frame — only explicit \n breaks lines.
  const indentReduction = (box.startIndent ?? 0) + (box.endIndent ?? 0);
  const paragraphs = new TextParagraphs(box, box.text ?? '');
  const wrapRule = wrapRuleOf(
    box,
    paragraphs.anyJustified(),
    () => shaper.measureAdvance(fontRef, ' ', fontSize, { ...opts, letterSpacing: 0 }) * hScale,
  );
  const wrapWidth = box.textMode === 'block' ? box.rect.width + wrapRule.slack - indentReduction : 0;
  const shapedWidthAt = (i: number): number => {
    if (areaSpans !== undefined && i < areaSpans.length) {
      const span = areaSpans[i];
      return span ? span.width + wrapRule.slack - indentReduction : 0;
    }

    return wrapWidth;
  };

  const segAdvance = (seg: CapsSegment): number => {
    if (seg.text.length === 0) return 0;
    const adv = shaper.measureAdvance(fontRef, seg.text, fontSize * seg.fontSizeScale, opts);
    return roundAdvance ? Math.round(adv) : adv;
  };

  // Трекинг живёт МЕЖДУ глифами: после последнего глифа строки Photoshop его срезает. Сверено с
  // растром эталонного PSD — правый край выключенной строки ложится ровно на границу контейнера,
  // а не на «граница − трекинг». Шейпер (как и opentype.js) добавляет letterSpacing и последнему
  // глифу, иначе рвался бы стык сегментов, поэтому лишний интервал снимается здесь, на строке.
  const trailingLetterSpacingPx = (segs: readonly CapsSegment[]): number => {
    for (let i = segs.length - 1; i >= 0; i--) {
      if (segs[i].text.length > 0) return letterSpacingRatio * fontSize * segs[i].fontSizeScale;
    }

    return 0;
  };

  const rawAdvanceOfSegs = (segs: readonly CapsSegment[]): number => segs.reduce((sum, s) => sum + segAdvance(s), 0);

  const lineAdvanceOfSegs = (segs: readonly CapsSegment[]): number =>
    Math.max(0, rawAdvanceOfSegs(segs) - trailingLetterSpacingPx(segs)) * hScale;

  const measureLineAdvance = (line: string): number =>
    lineAdvanceOfSegs(applyFontCapsSegments(line, box.fontCaps, smallCapSize, undefined));

  // Перо строки трекинг после последнего глифа НЕ срезает — срез живёт только на ПРАВОМ КРАЕ строки
  // (перенос/выравнивание/выключка). Позиция внутри строки (каретка) обязана мерить пером, иначе
  // встаёт на интервал левее глифа, перед которым стоит.
  const measurePrefixAdvance = (line: string): number =>
    rawAdvanceOfSegs(applyFontCapsSegments(line, box.fontCaps, smallCapSize, undefined)) * hScale;

  // Выключка двигает глифы после каждого пробела, а layoutRun отдаёт только glyphId — опознаём
  // пробел по его глифу. `.notdef` (0) = в шрифте пробела нет: -1 не совпадёт ни с одним глифом.
  const spaceGlyphId = paragraphs.gapAlign() === 'justify' ? shaper.charGlyphId(fontRef, JUSTIFY_SPACE) || -1 : -1;
  const gapChars = new Map<string, boolean>();
  const isJustifyGap = (ch: string): boolean => {
    if (spaceGlyphId === -1) return false;
    if (ch === JUSTIFY_SPACE) return true;
    // Шрифт, отдающий NBSP тем же глифом, что и пробел, раздвинет и его: считаем его интервалом,
    // иначе строка выедет за контейнер на неучтённую добавку.
    let gap = gapChars.get(ch);
    if (gap === undefined) {
      gap = shaper.charGlyphId(fontRef, ch) === spaceGlyphId;
      gapChars.set(ch, gap);
    }

    return gap;
  };

  return {
    fontSize,
    baseShift,
    lineHeightPx,
    capHeightPx,
    yGap,
    opts,
    hScale,
    vScale,
    scaleX,
    scaleY,
    smallCapSize,
    roundAdvance,
    canvasLeft: Math.round(box.rect.x),
    canvasTop: Math.round(box.rect.y),
    areaSpans,
    areaSpanAt,
    indentReduction,
    startIndent: box.startIndent ?? 0,
    firstLineIndent: box.firstLineIndent ?? 0,
    wrapWidth,
    shapedWidthAt,
    wrapRule,
    measureBreakAdvance: breakMeasure(wrapRule, measureLineAdvance, isJustifyGap),
    segAdvance,
    lineAdvanceOfSegs,
    measureLineAdvance,
    measurePrefixAdvance,
    spaceGlyphId,
    isJustifyGap,
    paragraphs,
  };
}

/**
 * Перо многоранговой строки — ОДИН источник и для её ширины (выравнивание/выключка), и для позиций
 * глифов, и для каретки инлайн-редактора.
 *
 * FKS-11074: перо жило внутри отрисовки, а `planTextLayout` (сетка каретки) мерил строку box-метриками.
 * Ран со своим кеглем/трекингом/капсом двигает перо на ПОЛНЫЕ ширины глифов, поэтому каретка вставала
 * не там, где курсор, а клик по такой строке отдавал чужой индекс.
 */
interface RunSpanPen {
  /** Шрифт, которым шейпится ран: свой `fontRef` (зеркало Rust `RunFaces`), иначе шрифт бокса. */
  fontRefOf(run: TextRun): string;
  /** Разбиение строки на спаны ранов (с учётом caps) — то же, по которому шейпятся глифы. */
  spansOf(lineText: string, lineCharStart: number, hyphenated?: boolean): RunSpan[];
  /** Кегль спана: `run.fontSize` (или box), поправленный на super/subscript и small-caps. */
  fontSizeOf(seg: RunSpan): number;
  /** x старта каждого спана и правый край набора (кернинг стыков ранов включён). */
  place(segs: readonly RunSpan[]): { starts: number[]; penEnd: number };
  /** Ширина строки в canvas-px: перо минус хвостовой трекинг (как в single-run пути). */
  lineAdvance(segs: readonly RunSpan[]): number;
  /** Перо после префикса строки, БЕЗ среза хвостового трекинга — там стоит каретка. */
  prefixAdvance(prefix: string, lineCharStart: number): number;
}

function runSpanPen(box: RenderTextBox, shaper: Shaper, fontRef: string, geo: TextBoxGeometry, runs: TextRun[]): RunSpanPen {
  const { roundAdvance, hScale, smallCapSize } = geo;
  const ownFonts = textLayoutAtLeast(box.textLayout, TEXT_LAYOUT_RUN_FONTS);
  const fontRefOf = (run: TextRun): string => (ownFonts && run.fontRef) || fontRef;

  const segAdvanceMulti = (seg: RunSpan, segOpts: FontLayoutOptions, segFontSize: number): number => {
    if (seg.text.length === 0) return 0;
    const adv = shaper.measureAdvance(fontRefOf(seg.run), seg.text, segFontSize, segOpts);
    return roundAdvance ? Math.round(adv) : adv;
  };

  const fontSizeOf = (seg: RunSpan): number => resolveRunFontSize(seg.run, box) * seg.fontSizeScale;

  const boundaryKernCache = new Map<string, number>();

  /**
   * Кернинг пары, разорванной ГРАНИЦЕЙ РАНОВ. Шейпер получает спаны порознь, поэтому пары
   * «последний глиф левого спана + первый глиф правого» для него не существует и её кернинг молча
   * пропадает — строка становится шире ровно на него.
   *
   * Photoshop эту пару кернит, и по настройке ПРАВОГО символа: в PSD kerning лежит НА символе и
   * относится к паре, ВХОДЯЩЕЙ в него. Эталон pw-fks11049-05.psd: 'Т' стоит отдельным раном с
   * `autoKerning: false`, но в растре PS пара «Та» поджата на −50/1000 em, как и в тексте без ранов.
   *
   * Значение берём разностью двух замеров пары (с кернингом и без) — так оно живёт на любом шейпере
   * (rustybuzz/opentype.js) без отдельного API пар. letterSpacing в разности сокращается.
   */
  const boundaryKernPx = (prev: RunSpan, next: RunSpan): number => {
    const segFontSize = fontSizeOf(next);
    // Разный кегль по бокам стыка — общей пары нет: PS начинает набор заново.
    if (segFontSize !== fontSizeOf(prev)) return 0;
    const prevChars = [...prev.text];
    const prevCh = prevChars[prevChars.length - 1];
    const nextCh = [...next.text][0];
    if (prevCh === undefined || nextCh === undefined) return 0;
    const opts = resolveRunOpts(next.run, box);
    const pairFont = fontRefOf(next.run);
    // Пара из двух шрифтов общего кернинга не имеет.
    if (opts.kerning === false || fontRefOf(prev.run) !== pairFont) return 0;

    const key = JSON.stringify([pairFont, prevCh, nextCh, segFontSize, opts.features ?? null, opts.opticalKern ?? null]);
    const cached = boundaryKernCache.get(key);
    if (cached !== undefined) return cached;

    const pair = prevCh + nextCh;
    const delta =
      shaper.measureAdvance(pairFont, pair, segFontSize, opts) -
      shaper.measureAdvance(pairFont, pair, segFontSize, { ...opts, kerning: false });
    const kern = roundAdvance ? Math.round(delta) : delta;
    boundaryKernCache.set(key, kern);
    return kern;
  };

  const place = (segs: readonly RunSpan[]): { starts: number[]; penEnd: number } => {
    const starts: number[] = [];
    let pen = 0;
    let prev: RunSpan | undefined;
    for (const seg of segs) {
      if (prev !== undefined) pen += boundaryKernPx(prev, seg);
      starts.push(pen);
      pen += segAdvanceMulti(seg, resolveRunOpts(seg.run, box), fontSizeOf(seg));
      if (seg.text.length > 0) prev = seg;
    }

    return { starts, penEnd: pen };
  };

  // Тот же срез трекинга после последнего глифа строки, что и в single-run пути (см. textBoxGeometry).
  const trailingLetterSpacing = (segs: readonly RunSpan[]): number => {
    for (let i = segs.length - 1; i >= 0; i--) {
      const seg = segs[i];
      if (seg.text.length === 0) continue;
      const ls = resolveRunOpts(seg.run, box).letterSpacing ?? 0;
      return ls * resolveRunFontSize(seg.run, box) * seg.fontSizeScale;
    }

    return 0;
  };

  const spansOf = (lineText: string, lineCharStart: number, hyphenated = false): RunSpan[] =>
    buildRunSpans(lineText, lineCharStart, hyphenated, runs, box.fontCaps, smallCapSize);

  return {
    fontRefOf,
    spansOf,
    fontSizeOf,
    place,
    lineAdvance: segs => Math.max(0, place(segs).penEnd - trailingLetterSpacing(segs)) * hScale,
    prefixAdvance: (prefix, lineCharStart) => place(spansOf(prefix, lineCharStart)).penEnd * hScale,
  };
}

/** Строка после переноса со всей геометрией, общей для отрисовки глифов и каретки инлайн-редактора. */
export interface PlannedTextLine {
  text: string;
  /** Начало строки в ИСХОДНОМ тексте — перенос съедает ведущие пробелы, восстановить снаружи нельзя. */
  charStart: number;
  segs: CapsSegment[];
  /** Ширина строки в canvas-px (hScale учтён, residual scaleX — нет). */
  advance: number;
  /** Левый край строки до scaleX: полоса контура + выравнивание + отступы. */
  xOffset: number;
  /** Добавка выключки на каждый пробел строки; 0 — строка не выключается. */
  justifyDelta: number;
  /**
   * Строка кончается ЖЁСТКИМ переводом (`\n`, с textLayout 7 — и мягким переносом U+2028) или это последняя
   * строка текста — в отличие от переноса по ширине, где символа за концом строки нет. Потребитель — подсветка
   * выделения: браузер красит и сам перевод строки, а перенос по ширине красить нечем.
   */
  paragraphEnd: boolean;
  /** Baseline under the frame-top seat, before `yGap`/`ascenderDelta` — see {@link lineBaselines}. */
  baseline: number;
  /** Строка оборвана переносом по слогам: последний символ `text` — дефис раскладки, в исходном тексте его нет. */
  hyphenated: boolean;
}

/** Полная раскладка строк однорангового (без `box.runs`) текста — SSoT рендера глифов и каретки. */
export interface TextLinePlan {
  lines: PlannedTextLine[];
  /** Per line: its paragraph and how it is placed (one box set below `TEXT_LAYOUT_PARAGRAPHS`). */
  lineParagraphs: LineParagraph[];
  lineHeightPx: number;
  capHeightPx: number;
  yGap: number;
  ascenderDelta: number;
  /** How far a point text's first baseline rises under a vertical scale — see {@link firstBaselineLift}. */
  baselineLift: number;
  verticalOffset: number;
  baseShift: number;
  fontSize: number;
  hScale: number;
  vScale: number;
  scaleX: number;
  scaleY: number;
  canvasLeft: number;
  canvasTop: number;
  inkHeight: number;
  /** Ширина самой длинной строки (canvas px, до residual scaleX) — та же, по которой считается выравнивание. */
  contentWidth: number;
  /** Рамка выравнивания строк: block — max(rect, content), free — rect в пространстве advance'ов. */
  alignWidth: number;
  /** Строки разложены по полосам area-контура (а не по прямоугольнику рамки). */
  shaped: boolean;
  /** Bands the lines sit in (area contour, or the wrap slots around page objects); `undefined` — the frame. */
  areaSpans?: readonly (AreaLineSpan | null)[];
  opts: ShaperOpts;
  segAdvance: (seg: CapsSegment) => number;
  measureLineAdvance: (line: string) => number;
  /**
   * Перо после префикса строки — без среза хвостового трекинга, ровно там, где стоит глиф.
   * `lineCharStart` — начало строки в исходном тексте: по нему разрешаются раны (`box.runs`).
   */
  measurePrefixAdvance: (prefix: string, lineCharStart: number) => number;
  /** Символ, который выключка раздвинет: тот же предикат, по которому посчитана {@link PlannedTextLine.justifyDelta}. */
  isJustifyGap: (ch: string) => boolean;
  smallCapSize: number;
  fontCaps: number | undefined;
}

/**
 * x of a line's pen: contour band edge, its paragraph's alignment, its indent — mirror of Rust `line_x_offset`. From
 * {@link TEXT_LAYOUT_INDENT_BAND} a right or centre line aligns in the band between the indents (start, end, first on a
 * paragraph's first line); a line wider than an indented band starts at its start indent. With every indent zero the
 * band is the frame and the step lays out as the steps below it, bit for bit.
 */
function lineXOffset(
  span: AreaLineSpan | null,
  shaped: boolean,
  rectWidth: number,
  alignWidth: number,
  adv: number,
  lp: LineParagraph,
  textLayout: number | undefined,
): number {
  const lineAlignWidth = alignWidthForLine(span, shaped, rectWidth, alignWidth);
  const lineBase = span ? span.x0 : 0;
  const { align, startIndent, endIndent, firstLineIndent } = lp.attrs;
  const first = lp.first ? firstLineIndent : 0;
  if (textLayoutAtLeast(textLayout, TEXT_LAYOUT_INDENT_BAND)) {
    const indents = startIndent + endIndent + first;
    const free = lineAlignWidth - indents - adv;
    const aligned = align === 'center' ? free / 2 : align === 'right' ? free : 0;
    // Any one indent, not their sum: `start = -end` still makes an indented band.
    const indented = startIndent !== 0 || endIndent !== 0 || first !== 0;
    return lineBase + (indented ? Math.max(0, aligned) : aligned) + startIndent + first;
  }

  const aligned = align === 'center' ? (lineAlignWidth - adv) / 2 : align === 'right' ? lineAlignWidth - adv : 0;
  // Same summation order as the legacy `lineBase + aligned + startIndent + firstLineIndent`: a stored box keeps its bits.
  return lineBase + aligned + startIndent + first;
}

/**
 * Frame top to first baseline the document pins on a block (mirror of the block branch of Rust `pinned_first_baseline`),
 * as the cap height it moves the seat by; undefined when nothing pins it. The wrap slots search with it before the first
 * line is known.
 */
function pinnedSeatDelta(
  box: RenderTextBox,
  shaper: Shaper,
  fontRef: string,
  fontSize: number,
  capHeightPx: number,
): number | undefined {
  const em = box.firstBaselineOffset;
  if (textLayoutAtLeast(box.textLayout, TEXT_LAYOUT_FRAME_BASELINE) && em !== undefined && Number.isFinite(em)) {
    return capHeightPx - em * box.fontSize;
  }

  if (box.firstBaselineRule !== 'ascent') return undefined;
  const yMax = shaper.glyphYMax(fontRef, 'd');
  if (shaper.charGlyphId(fontRef, 'd') === 0 || !(yMax > 0)) return undefined;
  return capHeightPx - (yMax / (shaper.unitsPerEm(fontRef) || 1000)) * fontSize;
}

/** Passes of the wrap around page objects while a line's leading or band grows (Rust `MAX_SEAT_PASSES`). */
const MAX_WRAP_PASSES = 4;

/** The lines of the box and, around page objects, where each one sits. */
interface BoxLines {
  wrapped: WrappedLine[];
  around?: WrapPlan;
  /** Around page objects with no pinned first baseline: the first-line pull-up the slots were seated by. */
  ascenderDelta?: number;
}

/**
 * Lines of the box: around page objects (`wrapZones`), by paragraphs from `TEXT_LAYOUT_PARAGRAPHS` (a multi-run
 * candidate measured by its runs — the pen its glyphs are placed by), else the legacy `\n` wrap with the box measure.
 * Mirror of the Rust wrap branch.
 */
function wrapBoxLines(
  box: RenderTextBox,
  text: string,
  shaper: Shaper,
  fontRef: string,
  geo: TextBoxGeometry,
  pen: RunSpanPen | undefined,
): BoxLines {
  const hyphenation = hyphenationRulesOf(box);
  const languages = wordLanguagesOf(box, text);
  const measureAt = pen
    ? (line: string, start: number): number => pen.lineAdvance(pen.spansOf(line, start, false))
    : (line: string): number => geo.measureLineAdvance(line);
  const zones = geo.paragraphs.wrapZones(geo.areaSpans !== undefined);
  if (zones) {
    const runs = box.runs?.length ? box.runs : undefined;
    // What a line turned out to need on a pass, by line index: the next pass gives it at least that (only ever up).
    const hints: { leading: number; ascent: number }[] = [];
    // The line is not known before its slot: it steps by the leading of the run its first word starts in, and on the
    // next pass by the largest of its runs, as without zones.
    const leadingAt = (i: number, start: number): number => {
      const run = runs ? findRunForChar(start, runs) : undefined;
      const own = run?.lineHeightPx !== undefined && run.lineHeightPx > 0 ? run.lineHeightPx : geo.lineHeightPx;
      return Math.max(own, hints[i]?.leading ?? 0);
    };

    // A line tests the zones by the cap of its tallest run and the size of the run it starts in, each on the run's font
    // and size (caps size included), never by less than the block's. A run's cap is drawn scaled by its vertical scale
    // and raised by its baseline shift.
    const blockInk: LineInk = { ascent: geo.capHeightPx, size: geo.fontSize };
    const spanInk = (p: RunSpanPen, seg: RunSpan): LineInk => {
      const size = p.fontSizeOf(seg);
      const cap = resolveCapHeightRatio(shaper, p.fontRefOf(seg.run)) * size;
      return { ascent: cap * (seg.run.verticalScale ?? geo.vScale) - resolveRunBaseShift(seg.run, box), size };
    };

    const lineInk = (p: RunSpanPen, segs: readonly RunSpan[]): LineInk | undefined =>
      segs
        .filter(seg => seg.text.length > 0)
        .reduce<LineInk | undefined>((ink, seg) => (ink ? maxInk(ink, spanInk(p, seg)) : spanInk(p, seg)), undefined);
    // Only the run a line starts in is known before its slot; the cap a line turned out to need lifts its band on the
    // next pass. Its size does not: a hint by line index would keep a sliver from the line that would fit there.
    // A block of one run: its cap is drawn scaled by the block's vertical scale and raised by its super/subscript shift.
    const soleInk: LineInk = {
      ascent: Math.max(geo.capHeightPx, geo.capHeightPx * geo.vScale - geo.baseShift),
      size: geo.fontSize,
    };
    const inkAt = (i: number, start: number): LineInk => {
      const own = pen && runs ? lineInk(pen, pen.spansOf(text.charAt(start), start)) : undefined;
      const ink = runs ? (own ? maxInk(blockInk, own) : blockInk) : soleInk;
      return { ascent: Math.max(ink.ascent, hints[i]?.ascent ?? 0), size: ink.size };
    };

    // Unpinned, the block is drawn pulled up by its first line's ink (`firstLineCapMetrics`): the slots seat by it too,
    // from the text's first char (always on that line) on, and again while the line inks higher. Mirror of Rust.
    const pinned = pinnedSeatDelta(box, shaper, fontRef, geo.fontSize, geo.capHeightPx);
    const segsOf = (line: string, start: number, hyphenated: boolean): ReadonlyArray<CapsSegment> =>
      pen ? pen.spansOf(line, start, hyphenated) : applyFontCapsSegments(line, box.fontCaps, geo.smallCapSize, undefined);
    const pullUp = (segs: ReadonlyArray<CapsSegment>): number =>
      firstLineCapMetrics(segs, shaper, fontRef, geo.fontSize, isInkTopAnchored(box)).ascenderDeltaPx;
    let delta = pinned ?? pullUp(segsOf(text.charAt(0), 0, false));
    const placed = (wrapped: WrappedLine[], plan: WrapPlan): BoxLines => ({
      wrapped,
      around: plan,
      ascenderDelta: pinned === undefined ? delta : undefined,
    });
    // Set the lines again while a line's leading or band grows or the first line inks higher (Rust also reseats the
    // first line by its runs).
    for (let pass = 1; ; pass++) {
      const metrics: SlotMetrics = {
        frameW: box.rect.width,
        frameH: box.rect.height,
        seat: geo.yGap + delta,
        ascent: geo.capHeightPx,
        size: geo.fontSize,
        firstBaseline: geo.lineHeightPx,
      };
      const { wrapped, plan } = geo.paragraphs.wrapAround(
        text,
        measureAt,
        geo.isJustifyGap,
        geo.wrapRule,
        zones,
        metrics,
        leadingAt,
        inkAt,
        hyphenation,
        languages,
      );
      if (pass >= MAX_WRAP_PASSES) return placed(wrapped, plan);
      const starts = computeLineCharStarts(text, wrapped, geo.paragraphs.byParagraph);
      const first = wrapped[0];
      const drawn = pinned === undefined && first ? pullUp(segsOf(first.text, starts[0], first.hyphenated === true)) : delta;
      const inksHigher = drawn < delta - 1e-3;
      if (inksHigher) delta = drawn;
      if (!pen || !runs) {
        if (inksHigher) continue;
        return placed(wrapped, plan);
      }

      const lines = wrapped.map((l, i) => {
        const segs = pen.spansOf(l.text, starts[i], l.hyphenated === true);
        const used = { leading: leadingAt(i, starts[i]), ascent: inkAt(i, starts[i]).ascent };
        // A line with no ink tests no zone: its cap asks for nothing.
        const ink = lineInk(pen, segs);
        const needed = {
          leading: runLineLeading(segs, starts[i], runs, geo.lineHeightPx),
          ascent: ink ? Math.max(ink.ascent, geo.capHeightPx) : 0,
        };
        return { used, needed };
      });
      let grew = false;
      lines.forEach(({ used, needed }, i) => {
        grew ||= needed.leading > used.leading || needed.ascent > used.ascent;
        const h = hints[i];
        hints[i] = { leading: Math.max(h?.leading ?? 0, needed.leading), ascent: Math.max(h?.ascent ?? 0, needed.ascent) };
      });
      if (!grew && !inksHigher) return placed(wrapped, plan);
    }
  }

  if (geo.paragraphs.byParagraph) {
    return {
      wrapped: geo.paragraphs.wrap(text, measureAt, geo.isJustifyGap, geo.wrapRule, geo.areaSpans, hyphenation, languages),
    };
  }

  const rawLines = text.split('\n');
  return {
    wrapped: geo.areaSpans
      ? wrapLinesShapedDetailed(rawLines, geo.measureBreakAdvance, geo.shapedWidthAt, hyphenation, languages)
      : wrapLinesDetailed(rawLines, geo.measureBreakAdvance, geo.wrapWidth, hyphenation, languages),
  };
}

/** How the lines are placed, and the bands they sit in: around page objects each line's slot stands in for an area band. */
interface PlacedLines {
  lineParagraphs: LineParagraph[];
  areaSpans: readonly (AreaLineSpan | null)[] | undefined;
  areaSpanAt: (i: number) => AreaLineSpan | null;
}

function placeLines(geo: TextBoxGeometry, { wrapped, around }: BoxLines): PlacedLines {
  const lineParagraphs = geo.paragraphs.lines(wrapped);
  if (!around) return { lineParagraphs, areaSpans: geo.areaSpans, areaSpanAt: geo.areaSpanAt };
  const spans = around.spans;
  return { lineParagraphs: around.place(lineParagraphs), areaSpans: spans, areaSpanAt: i => spans[i] ?? null };
}

/** Wrap budget of each laid-out line, the one a line wider than it spills over (legacy: the box budget). */
function wrapBudgetAt(
  geo: TextBoxGeometry,
  lineParagraphs: readonly LineParagraph[],
  areaSpans: readonly (AreaLineSpan | null)[] | undefined,
): (i: number) => number {
  return geo.paragraphs.byParagraph ? geo.paragraphs.budgetOf(lineParagraphs, geo.wrapRule, areaSpans) : geo.shapedWidthAt;
}

function planTextLines(box: RenderTextBox, shaper: Shaper, fontRef: string, geo: TextBoxGeometry): TextLinePlan {
  const text = box.text ?? '';
  // Многоранговый текст меряется ТЕМ ЖЕ пером, каким его рисует multi-run путь: перенос ниже
  // TEXT_LAYOUT_PARAGRAPHS box-метричный (зеркально Rust), с неё — тоже по ранам; ширина строки, выключка и
  // позиция каретки внутри строки — per-run, иначе ран со своим кеглем уводит каретку на ширины глифов.
  const pen = box.runs ? runSpanPen(box, shaper, fontRef, geo, box.runs) : undefined;
  const boxLines = wrapBoxLines(box, text, shaper, fontRef, geo, pen);
  const { wrapped, around } = boxLines;
  const lines = wrapped.map(l => l.text);
  const charStarts = computeLineCharStarts(text, wrapped, geo.paragraphs.byParagraph);
  const { lineParagraphs, areaSpans, areaSpanAt } = placeLines(geo, boxLines);

  // Caps segments per line: normal / all-caps (fontCaps>=2) / faux small-caps (fontCaps==1). font=undefined
  // forces faux small-caps because the engine renders outlines by glyph-id and can't apply the smcp feature.
  const lineRunSpans = pen ? lines.map((line, i) => pen.spansOf(line, charStarts[i], wrapped[i].hyphenated === true)) : undefined;
  const lineSegments: CapsSegment[][] =
    lineRunSpans ?? lines.map(line => applyFontCapsSegments(line, box.fontCaps, geo.smallCapSize, undefined));

  // Мера строки ТЕМ ЖЕ пером, каким её рисуют: multi-run считает по ранам, single-run — box-метрикой.
  const measureLine: (visible: string, lineIndex: number) => number = pen
    ? (visible, i) => pen.lineAdvance(pen.spansOf(visible, charStarts[i], wrapped[i].hyphenated === true))
    : visible => geo.measureLineAdvance(visible);

  // flowing mode aligns to the container width to fold in the whole-block shift the editor applies there
  // (else right/center lines would not match Konva); fixed mode aligns to max(container, content).
  const lineAdvances = lines.map((line, i) => {
    // Хвостовые пробелы висят за краем строки, кроме point-текста по TEXT_LAYOUT_PEN_LINES.
    // Без хвоста мера уже посчитана сегментами.
    const visible = alignedLineText(box, line);
    if (visible.length === line.length) {
      return pen && lineRunSpans ? pen.lineAdvance(lineRunSpans[i]) : geo.lineAdvanceOfSegs(lineSegments[i]);
    }

    return measureLine(visible, i);
  });
  const contentWidth = lineAdvances.length ? Math.max(0, ...lineAdvances) : 0;
  // Point-текст рамки переноса не имеет — якорь стоит на краю выравнивания, ширина содержимого на него не влияет.
  // rect.width в canvas-px, adv — до residual scaleX (его добавляет отрисовка), поэтому рамку переводим в их пространство.
  const alignWidth = box.textMode === 'block' ? Math.max(box.rect.width, contentWidth) : box.rect.width / (geo.scaleX || 1);
  // startIndent shifts every line right; firstLineIndent shifts the paragraph's first (PSD ParagraphStyle).
  const xOffsets = lineAdvances.map((adv, i) =>
    lineXOffset(areaSpanAt(i), areaSpans !== undefined, box.rect.width, alignWidth, adv, lineParagraphs[i], box.textLayout),
  );

  const justifyDeltas = justifyDeltasForLines(
    box,
    geo.paragraphs,
    wrapped,
    lineParagraphs,
    areaSpanAt,
    measureLine,
    geo.isJustifyGap,
    geo.wrapRule,
  );

  // Decorative-font cap pull-up, from the fontCaps-applied first line — the SAME source the caret overlay consumes.
  // Beside objects the block seats where its slot plan checked it.
  const ascenderDelta =
    boxLines.ascenderDelta ??
    firstLineCapMetrics(lineSegments[0] ?? [], shaper, fontRef, geo.fontSize, isInkTopAnchored(box)).ascenderDeltaPx;
  const baselineLift = firstBaselineLift(box, geo.capHeightPx - ascenderDelta, geo.vScale);

  // Konva parity (svg-text-generator): centre/bottom distribute blank space against the INK height —
  // max(N·lineHeight, glyph bottom of EVERY line) — a non-last line's descender can be the deepest
  // (e.g. tight leading), so each line's own ink bottom is checked, not just the last line's.
  const baselines = around
    ? [...around.baselines]
    : lineBaselines(box, lineParagraphs, geo.lineHeightPx, geo.areaSpans !== undefined, i =>
        lineRunSpans && box.runs ? runLineLeading(lineRunSpans[i], charStarts[i], box.runs, geo.lineHeightPx) : geo.lineHeightPx,
      );
  const upm = shaper.unitsPerEm(fontRef) || 1000;
  let maxInkBottomPx = baselines.length ? baselines[baselines.length - 1] : 0;
  lineSegments.forEach((segs, i) => {
    let lineDescentPx = 0;
    for (const seg of segs) {
      const segFontSize = pen && isRunSpan(seg) ? pen.fontSizeOf(seg) : geo.fontSize * seg.fontSizeScale;
      const segVScale = (isRunSpan(seg) ? seg.run.verticalScale : undefined) ?? geo.vScale;
      for (const ch of seg.text) {
        const d = (-shaper.glyphYMin(fontRef, ch) / upm) * segFontSize * segVScale;
        if (d > lineDescentPx) lineDescentPx = d;
      }
    }

    const lineBottomPx = baselines[i] + lineDescentPx;
    if (lineBottomPx > maxInkBottomPx) maxInkBottomPx = lineBottomPx;
  });

  const inkHeight = maxInkBottomPx - geo.yGap - baselineLift;
  const vAlign = resolveVerticalAlign(box.verticalAlign);
  // areaShape привязывает строки к полосам контура — вертикальный сдвиг сломал бы соответствие.
  const verticalOffset = areaSpans
    ? 0
    : vAlign === 'center'
      ? (box.rect.height - inkHeight) / 2
      : vAlign === 'bottom'
        ? box.rect.height - inkHeight
        : 0;

  return {
    lines: lines.map((lineText, i) => ({
      text: lineText,
      charStart: charStarts[i],
      segs: lineSegments[i],
      advance: lineAdvances[i],
      xOffset: xOffsets[i],
      justifyDelta: justifyDeltas?.[i] ?? 0,
      paragraphEnd: isForcedLineEnd(wrapped[i]),
      baseline: baselines[i],
      hyphenated: wrapped[i].hyphenated === true,
    })),
    lineParagraphs,
    lineHeightPx: geo.lineHeightPx,
    capHeightPx: geo.capHeightPx,
    yGap: geo.yGap,
    ascenderDelta,
    baselineLift,
    verticalOffset,
    baseShift: geo.baseShift,
    fontSize: geo.fontSize,
    hScale: geo.hScale,
    vScale: geo.vScale,
    scaleX: geo.scaleX,
    scaleY: geo.scaleY,
    canvasLeft: geo.canvasLeft,
    canvasTop: geo.canvasTop,
    inkHeight,
    contentWidth,
    alignWidth,
    shaped: areaSpans !== undefined,
    areaSpans,
    opts: geo.opts,
    segAdvance: geo.segAdvance,
    measureLineAdvance: geo.measureLineAdvance,
    measurePrefixAdvance: pen ? pen.prefixAdvance : prefix => geo.measurePrefixAdvance(prefix),
    isJustifyGap: geo.isJustifyGap,
    smallCapSize: geo.smallCapSize,
    fontCaps: box.fontCaps,
  };
}

/**
 * Раскладка строк бокса — та же, по которой рисуются глифы. Каретка инлайн-редактора берёт сетку строк
 * отсюда (см. `area-caret-layout`). Многоранговый текст меряется тем же пером, что и multi-run отрисовка
 * (кегль/трекинг/капсы рана); box-метрикой ниже `TEXT_LAYOUT_PARAGRAPHS` остаётся только БЮДЖЕТ ПЕРЕНОСА — зеркально Rust.
 * `undefined` — у бокса нет текста / он идёт по кривой / вертикальный: собственной сетки строк нет.
 */
export function planTextLayout(box: RenderTextBox, shaper: Shaper, fontRef: string): TextLinePlan | undefined {
  if (box.textPath?.d || box.orientation === 'vertical') return undefined;
  return planTextLines(box, shaper, fontRef, textBoxGeometry(box, shaper, fontRef));
}

/**
 * The decoration a line stretch carries — the run's flag wins over the box's, as in the Rust adapter. Every stretch
 * gets one, flagged or not, so the lines see the stretches between them (hidden tabs, plain runs) as Rust does.
 * `x1` is filled once the stretch's glyphs (and the justification they spread) are placed.
 */
function stretchDecoration(
  run: TextRun | undefined,
  box: RenderTextBox,
  line: number,
  x0: number,
  baseline: number,
  fontSize: number,
): GlyphDecoration {
  return {
    underline: (run?.underline ?? box.underline) === true,
    strikethrough: (run?.strikethrough ?? box.strikethrough) === true,
    line,
    x0,
    x1: x0,
    baseline,
    fontSize,
    yUnderline: run?.yUnderline ?? box.yUnderline ?? 1,
    color: run?.color ?? box.color,
  };
}

/** Hands the layout's stretches to every glyph when any of them draws a line; a layout without one stays as it was. */
function markDecorations(glyphs: LaidOutGlyph[], stretches: GlyphDecoration[]): void {
  if (!stretches.some(d => d.underline || d.strikethrough)) return;
  for (const g of glyphs) g.decorations = stretches;
}

/**
 * The box case the layout draws. A path wins over the orientation in both dispatchers (here and Rust
 * `layout_text_glyphs_traced`): path text draws only all caps (`planGlyphsOnPath`, Rust `layout_on_path`), vertical text
 * off a path draws in no case (`layoutGlyphsVertical`, Rust `layout_vertical`), the rest draws the box's case.
 */
export function boxCaseTheLayoutDraws(box: Pick<RenderTextBox, 'orientation' | 'textPath' | 'fontCaps'>): number | undefined {
  if (box.textPath?.d) return (box.fontCaps ?? 0) >= 2 ? box.fontCaps : undefined;
  if (box.orientation === 'vertical') return undefined;
  return box.fontCaps;
}

export function layoutGlyphsBackend(
  box: RenderTextBox,
  shaper: Shaper,
  fontRef: string,
  metrics?: TextLayoutMetrics,
): LaidOutGlyph[] {
  const text = box.text ?? '';
  if (!text) return [];
  if (box.textPath?.d) return layoutGlyphsOnPath(box, shaper, fontRef);
  if (box.orientation === 'vertical') return layoutGlyphsVertical(box, shaper, fontRef);

  const geo = textBoxGeometry(box, shaper, fontRef);
  const { fontSize, baseShift, lineHeightPx, yGap, opts, hScale, vScale, scaleX, scaleY, canvasLeft, canvasTop, spaceGlyphId } =
    geo;
  const out: LaidOutGlyph[] = [];

  // Single-run (no box.runs): every helper uses box-level values, byte-identical to the original path.
  if (!box.runs) {
    const plan = planTextLines(box, shaper, fontRef, geo);
    const { ascenderDelta, baselineLift, verticalOffset } = plan;
    const stretches: GlyphDecoration[] = [];

    plan.lines.forEach((line, lineIndex) => {
      const { xOffset, justifyDelta, baseline } = line;
      // Копится по глифам строки: каждый пройденный пробел раздвигает всё, что правее него.
      let justifyShift = 0;
      let segX = 0; // line-local pen position (segments may differ in fontSize for small-caps)

      for (const seg of line.segs) {
        const start = segX;
        segX += geo.segAdvance(seg);
        if (seg.text.length === 0) continue;
        const segFontSize = fontSize * seg.fontSizeScale;
        // The line keeps the box size: small-caps segments of one text draw one line.
        const decoration = stretchDecoration(
          undefined,
          box,
          lineIndex,
          canvasLeft + (start * hScale + xOffset + justifyShift) * scaleX,
          canvasTop + verticalOffset + (baseline - yGap - ascenderDelta - baselineLift + baseShift) * scaleY,
          fontSize,
        );
        stretches.push(decoration);

        for (const glyph of shaper.layoutRun(fontRef, seg.text, segFontSize, baseline, opts)) {
          const { glyphId, x: gx } = glyph;
          const gy = aboutBaseline(glyph.y, baseline, vScale);
          // Скрытый таб не рисуется, но растягивается выключкой, если шрифт отдаёт ему глиф пробела — как в Rust.
          if (!isHiddenTabGlyph(seg.text, glyph)) {
            const laid: LaidOutGlyph = {
              fontRef,
              glyphId,
              x: canvasLeft + ((start + gx) * hScale + xOffset + justifyShift) * scaleX,
              y: canvasTop + verticalOffset + (gy - yGap - ascenderDelta - baselineLift + baseShift) * scaleY,
              scaleX: scaleX * hScale,
              scaleY: scaleY * vScale,
              fontSize: segFontSize,
            };
            out.push(laid);
          }

          if (justifyDelta !== 0 && glyphId === spaceGlyphId) justifyShift += justifyDelta;
        }

        decoration.x1 = canvasLeft + (segX * hScale + xOffset + justifyShift) * scaleX;
      }
    });
    markDecorations(out, stretches);

    if (metrics)
      fillMetrics(
        metrics,
        box,
        plan.inkHeight,
        plan.contentWidth,
        plan.alignWidth,
        blockWidthSpill(
          box,
          plan.lines.map(l => l.advance),
          wrapBudgetAt(geo, plan.lineParagraphs, plan.areaSpans),
        ),
        plan.lines.length,
      );
    return out;
  }

  // ─── Multi-run path ──────────────────────────────────────────────────────────────────────────
  // Wrap measurement below TEXT_LAYOUT_PARAGRAPHS uses box-level fontSize/opts (per-run fontSize affects layout but
  // not wrap width); from it a candidate measures by its runs — `wrapBoxLines`, mirror of Rust.
  const runs = box.runs;
  const pen = runSpanPen(box, shaper, fontRef, geo, runs);

  const boxLines = wrapBoxLines(box, text, shaper, fontRef, geo, pen);
  const { wrapped, around } = boxLines;
  const lines = wrapped.map(l => l.text);
  const lineCharStarts = computeLineCharStarts(text, wrapped, geo.paragraphs.byParagraph);
  const { lineParagraphs, areaSpans, areaSpanAt } = placeLines(geo, boxLines);
  const lineRunSpans = lines.map((line, i) => pen.spansOf(line, lineCharStarts[i], wrapped[i].hyphenated === true));

  // Та же мера строки, что и в planTextLines (`alignedLineText`).
  const measureRunLine = (visible: string, i: number): number =>
    pen.lineAdvance(pen.spansOf(visible, lineCharStarts[i], wrapped[i].hyphenated === true));
  const lineAdvances = lines.map((line, i) => {
    const visible = alignedLineText(box, line);
    return visible.length === line.length ? pen.lineAdvance(lineRunSpans[i]) : measureRunLine(visible, i);
  });
  const contentWidth = lineAdvances.length ? Math.max(0, ...lineAdvances) : 0;
  // Point-текст рамки переноса не имеет — якорь стоит на краю выравнивания, ширина содержимого на него не влияет.
  // rect.width в canvas-px, adv — до residual scaleX (его добавляет отрисовка), поэтому рамку переводим в их пространство.
  const alignWidth = box.textMode === 'block' ? Math.max(box.rect.width, contentWidth) : box.rect.width / (scaleX || 1);
  const xOffsets = lineAdvances.map((adv, i) =>
    lineXOffset(areaSpanAt(i), areaSpans !== undefined, box.rect.width, alignWidth, adv, lineParagraphs[i], box.textLayout),
  );

  // Бюджет ПЕРЕНОСА ниже TEXT_LAYOUT_PARAGRAPHS остаётся box-метрикой, а вот ширина для ВЫКЛЮЧКИ
  // обязана совпадать с раскладкой до последнего пикселя: перенос лишь выбирает точку разрыва, а
  // выключка растягивает пробелы «до края» — расхождение меры и пера уезжает во флэш один в один.
  const justifyDeltas = justifyDeltasForLines(
    box,
    geo.paragraphs,
    wrapped,
    lineParagraphs,
    areaSpanAt,
    measureRunLine,
    geo.isJustifyGap,
    geo.wrapRule,
  );

  // Same SSoT cap pull-up as the single-run path — the caret overlay reads it from `capMetricsForCaret`.
  const ascenderDelta =
    boxLines.ascenderDelta ??
    firstLineCapMetrics(lineRunSpans[0] ?? [], shaper, fontRef, fontSize, isInkTopAnchored(box)).ascenderDeltaPx;
  const baselineLift = firstBaselineLift(box, geo.capHeightPx - ascenderDelta, vScale);

  // Point frames already start at the shifted ink top. Normalize only the
  // explicit-shift contribution, preserving the existing no-shift geometry.
  let pointShiftAnchor = 0;
  if (box.textMode !== 'block') {
    let plainTop = -Infinity;
    let shiftedTop = -Infinity;
    const upm = shaper.unitsPerEm(fontRef) || 1000;
    for (const seg of lineRunSpans[0] ?? []) {
      const shift = seg.run.baselineShift ?? 0;
      const unshiftedBase = resolveRunBaseShift(seg.run, box) + shift;
      for (const ch of seg.text) {
        const ym = shaper.glyphYMax(fontRef, ch);
        if (ym <= shaper.glyphYMin(fontRef, ch)) continue;
        const ascent = (ym / upm) * pen.fontSizeOf(seg) * (seg.run.verticalScale ?? vScale) - unshiftedBase;
        plainTop = Math.max(plainTop, ascent);
        shiftedTop = Math.max(shiftedTop, ascent + shift);
      }
    }

    if (Number.isFinite(plainTop)) pointShiftAnchor = shiftedTop - plainTop;
  }

  // Konva parity: ink height = max(N·lineHeight, glyph bottom of EVERY line), matching the single-run block above.
  const baselines = around
    ? [...around.baselines]
    : lineBaselines(box, lineParagraphs, lineHeightPx, areaSpans !== undefined, i =>
        runLineLeading(lineRunSpans[i], lineCharStarts[i], runs, lineHeightPx),
      );
  const upm = shaper.unitsPerEm(fontRef) || 1000;
  let maxInkBottomPx = baselines.length ? baselines[baselines.length - 1] : 0;
  lineRunSpans.forEach((segs, i) => {
    let lineDescentPx = 0;
    for (const seg of segs) {
      const segFontSize = pen.fontSizeOf(seg);
      const runVScale = seg.run.verticalScale ?? vScale;
      for (const ch of seg.text) {
        const d = (-shaper.glyphYMin(fontRef, ch) / upm) * segFontSize * runVScale;
        if (d > lineDescentPx) lineDescentPx = d;
      }
    }

    const lineBottomPx = baselines[i] + lineDescentPx;
    if (lineBottomPx > maxInkBottomPx) maxInkBottomPx = lineBottomPx;
  });

  const inkHeight = maxInkBottomPx - yGap - baselineLift;
  const vAlign = resolveVerticalAlign(box.verticalAlign);
  // areaShape привязывает строки к полосам контура — вертикальный сдвиг сломал бы соответствие.
  const verticalOffset = areaSpans
    ? 0
    : vAlign === 'center'
      ? (box.rect.height - inkHeight) / 2
      : vAlign === 'bottom'
        ? box.rect.height - inkHeight
        : 0;

  const stretches: GlyphDecoration[] = [];
  lineRunSpans.forEach((segs, i) => {
    const baseline = baselines[i];
    const xOffset = xOffsets[i];
    const justifyDelta = justifyDeltas?.[i] ?? 0;
    let justifyShift = 0;
    // Ровно то перо, по которому померена ширина строки выше (включая кернинг на стыках ранов).
    const { starts } = pen.place(segs);

    for (let k = 0; k < segs.length; k++) {
      const seg = segs[k];
      const runOpts = resolveRunOpts(seg.run, box);
      const runBaseShift = resolveRunBaseShift(seg.run, box);
      const segFontSize = pen.fontSizeOf(seg);
      const runHScale = seg.run.horizontalScale ?? hScale;
      const runVScale = seg.run.verticalScale ?? vScale;
      const start = starts[k];
      if (seg.text.length === 0) continue;

      const glyphFill = seg.run.color !== undefined && seg.run.color !== box.color ? seg.run.color : undefined;
      const runFontRef = pen.fontRefOf(seg.run);
      // The run's size without the small-caps reduction: its caps segments draw one line.
      const decoration = stretchDecoration(
        seg.run,
        box,
        i,
        canvasLeft + (start * runHScale + xOffset + justifyShift) * scaleX,
        canvasTop + verticalOffset + (baseline - yGap - ascenderDelta - baselineLift + runBaseShift + pointShiftAnchor) * scaleY,
        resolveRunFontSize(seg.run, box),
      );
      stretches.push(decoration);

      for (const shaped of shaper.layoutRun(runFontRef, seg.text, segFontSize, baseline, runOpts)) {
        const { glyphId, x: gx } = shaped;
        const gy = aboutBaseline(shaped.y, baseline, runVScale);
        if (!isHiddenTabGlyph(seg.text, shaped)) {
          const glyph: LaidOutGlyph = {
            fontRef: runFontRef,
            glyphId,
            x: canvasLeft + ((start + gx) * runHScale + xOffset + justifyShift) * scaleX,
            y: canvasTop + verticalOffset + (gy - yGap - ascenderDelta - baselineLift + runBaseShift + pointShiftAnchor) * scaleY,
            scaleX: scaleX * runHScale,
            scaleY: scaleY * runVScale,
            fontSize: segFontSize,
          };
          if (glyphFill !== undefined) glyph.fill = glyphFill;
          out.push(glyph);
        }

        if (justifyDelta !== 0 && glyphId === spaceGlyphId) justifyShift += justifyDelta;
      }

      // A plain stretch only ends the open line: its span is never drawn, so it skips the re-measure.
      if (decoration.underline || decoration.strikethrough) {
        decoration.x1 = canvasLeft + ((start + pen.place([seg]).penEnd) * runHScale + xOffset + justifyShift) * scaleX;
      }
    }
  });
  markDecorations(out, stretches);

  if (metrics) {
    const spill = blockWidthSpill(box, lineAdvances, wrapBudgetAt(geo, lineParagraphs, areaSpans));
    fillMetrics(metrics, box, inkHeight, contentWidth, alignWidth, spill, lines.length);
  }

  return out;
}
